package dev.lingvo.site.components.studio.canvas

import dev.lingvo.shared.TextDirection
import dev.lingvo.shared.screenshots.doc.BlendMode
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.render.Affine
import dev.lingvo.shared.screenshots.render.CanvasAlign
import dev.lingvo.shared.screenshots.render.DeviceShadow
import dev.lingvo.shared.screenshots.render.FillRule
import dev.lingvo.shared.screenshots.render.ImageHandle
import dev.lingvo.shared.screenshots.render.Path
import dev.lingvo.shared.screenshots.render.PathOp
import dev.lingvo.shared.screenshots.render.ResolvedFont
import dev.lingvo.shared.screenshots.render.ResolvedPaint
import dev.lingvo.shared.screenshots.render.StrokeStyle
import dev.lingvo.shared.screenshots.render.Surface

/** Maps Surface to either DOM or OffscreenCanvas 2D contexts; the narrow dynamic boundary needs no DOM dependency in shared. */
class CanvasSurface(private val ctx: dynamic, locale: String? = null) : Surface {
    private val patterns = linkedMapOf<ResolvedPaint.Noise, Any>()
    override val width: Int get() = (ctx.canvas.width as Number).toInt()
    override val height: Int get() = (ctx.canvas.height as Number).toInt()

    init {
        ctx.imageSmoothingQuality = "high"
        if (locale != null) setLocale(locale)
    }

    /** Best-effort locale metadata helps native shaping select localized font forms. */
    fun setLocale(locale: String) {
        try { ctx.canvas.lang = locale } catch (_: Throwable) { /* Some worker contexts expose a sealed canvas. */ }
    }

    override fun save() { ctx.save() }
    override fun restore() { ctx.restore() }
    override fun concat(m: Affine) { ctx.transform(m.a, m.b, m.c, m.d, m.e, m.f) }
    override fun setBlend(mode: BlendMode) {
        ctx.globalCompositeOperation = when (mode) {
            BlendMode.NORMAL -> "source-over"
            BlendMode.MULTIPLY -> "multiply"
            BlendMode.SCREEN -> "screen"
            BlendMode.OVERLAY -> "overlay"
            BlendMode.SOFT_LIGHT -> "soft-light"
        }
    }
    override fun setAlpha(alpha: Double) { ctx.globalAlpha = alpha }
    override fun setShadow(shadow: DeviceShadow?) {
        ctx.shadowOffsetX = shadow?.dx ?: 0.0
        ctx.shadowOffsetY = shadow?.dy ?: 0.0
        ctx.shadowBlur = shadow?.blur ?: 0.0
        ctx.shadowColor = rgba(shadow?.argb ?: 0L)
    }
    override fun clip(path: Path, rule: FillRule) {
        replay(path)
        ctx.clip(rule.name.lowercase())
    }
    override fun fill(path: Path, paint: ResolvedPaint, rule: FillRule) {
        replay(path)
        ctx.fillStyle = canvasPaint(paint)
        ctx.fill(rule.name.lowercase())
    }
    override fun stroke(path: Path, paint: ResolvedPaint, style: StrokeStyle) {
        replay(path)
        ctx.strokeStyle = canvasPaint(paint)
        ctx.lineWidth = style.width
        ctx.lineJoin = style.join.name.lowercase()
        ctx.lineCap = style.cap.name.lowercase()
        ctx.setLineDash(style.dash.toTypedArray())
        ctx.stroke()
    }
    override fun drawImage(image: ImageHandle, src: Rect, dst: Rect) {
        require(image is BitmapImage) { "CanvasSurface requires a BitmapImage" }
        ctx.drawImage(image.bitmap, src.x, src.y, src.w, src.h, dst.x, dst.y, dst.w, dst.h)
    }
    override fun fillText(text: String, x: Double, y: Double, font: ResolvedFont, paint: ResolvedPaint,
        align: CanvasAlign, direction: TextDirection, letterSpacingPx: Double) {
        configureFont(ctx, font, letterSpacingPx)
        ctx.textAlign = align.name.lowercase()
        ctx.textBaseline = "alphabetic"
        ctx.direction = direction.html
        ctx.fillStyle = canvasPaint(paint)
        ctx.fillText(text, x, y)
    }

    private fun replay(path: Path) {
        ctx.beginPath()
        path.ops.forEach { op ->
            when (op) {
                is PathOp.Move -> ctx.moveTo(op.x, op.y)
                is PathOp.Line -> ctx.lineTo(op.x, op.y)
                is PathOp.Cubic -> ctx.bezierCurveTo(op.c1x, op.c1y, op.c2x, op.c2y, op.x, op.y)
                PathOp.Close -> ctx.closePath()
            }
        }
    }

    private fun canvasPaint(paint: ResolvedPaint): dynamic = when (paint) {
        is ResolvedPaint.Solid -> rgba(paint.argb)
        is ResolvedPaint.Linear -> {
            val gradient = ctx.createLinearGradient(paint.x0, paint.y0, paint.x1, paint.y1)
            paint.stops.forEach { (at, color) -> gradient.addColorStop(at, rgba(color)) }
            gradient
        }
        is ResolvedPaint.Radial -> {
            val gradient = ctx.createRadialGradient(paint.cx, paint.cy, 0, paint.cx, paint.cy, paint.r)
            paint.stops.forEach { (at, color) -> gradient.addColorStop(at, rgba(color)) }
            gradient
        }
        is ResolvedPaint.Noise -> patterns[paint] ?: run {
            val pattern: dynamic = noisePattern(ctx, paint.seed, paint.amount)
            if (patterns.size == 8) patterns.remove(patterns.keys.first())
            patterns[paint] = pattern.unsafeCast<Any>()
            pattern
        }
    }
}

private fun rgba(argb: Long): String =
    "rgba(${(argb shr 16) and 255},${(argb shr 8) and 255},${argb and 255},${((argb shr 24) and 255).toDouble() / 255})"
